import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import Panel from '../Panel';
import ApprovalModal from './ApprovalModal';
import RejectionModal from './RejectionModal';
import StatusBadge from './StatusBadge';
import {
    approveCampaignRequest,
    rejectCampaignRequest,
    createCampaignRequestNote,
} from '../../services/campaign';
import {
    CampaignRequest,
    Patient,
    ApplicationSerializer,
} from '~/types/campaigns';

interface PossibleMatch {
    campaign_id: number;
    name: string;
    email: string;
    address: string;
    medical_reason: string;
    phone: string;
    status: string;
    match_type: 'email' | 'name' | 'address' | 'phone';
    ntaf_id: string;
    email_match_type?: 'primary_contact' | 'client';
    matched_email?: string;
}

interface EmailMatchesSummary {
    has_email_matches: boolean;
    primary_contact_matches: PossibleMatch[];
    client_matches: PossibleMatch[];
}

interface Props {
    campaignRequest: CampaignRequest;
    patient: Patient | null;
    metadata?: any;
    application?: ApplicationSerializer | null;
    emailMatchesSummary?: EmailMatchesSummary | null;
    onCampaignRequestUpdate?: (updatedCampaignRequest: CampaignRequest) => void;
    onApplicationUpdate?: (application: ApplicationSerializer) => void;
    onNoteAdd?: (note: any) => void;
}

const NameHeader: React.FC<Props> = ({
    campaignRequest,
    patient,
    metadata,
    application,
    emailMatchesSummary,
    onCampaignRequestUpdate,
    onApplicationUpdate,
    onNoteAdd,
}) => {
    const navigate = useNavigate();
    const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [approvalError, setApprovalError] = useState<string | null>(null);
    const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);

    // Check email requirements for approval
    const isPrimarySelf = campaignRequest?.primary_relationship_to_client?.code === 'self';
    // When the primary contact is the client, the initial contact may still be a
    // different person (e.g. a relative submitting on the client's behalf), so
    // only fall back to initial_email when the initial contact IS the patient
    // (campaign_is_for === 'self'). Mirrors the backend approval logic.
    const isInitialContactThePatient = campaignRequest?.campaign_is_for?.code === 'self';
    const primaryContactEmailValue = isPrimarySelf
        ? (
              (isInitialContactThePatient
                  ? campaignRequest?.initial_email?.trim()
                  : '') ||
              campaignRequest?.patient_email?.trim() ||
              campaignRequest?.primary_email?.trim() ||
              ''
          )
        : campaignRequest?.primary_email?.trim() || '';
    const clientEmail = isPrimarySelf
        ? primaryContactEmailValue
        : campaignRequest?.patient_email?.trim() || '';

    // If primary contact is "self", client email is required (it's the only contact email).
    // Otherwise, primary contact email alone is sufficient.
    const hasRequiredEmail = isPrimarySelf
        ? Boolean(clientEmail)
        : Boolean(primaryContactEmailValue);

    // Check if CSC is assigned
    const hasAssignedCSC = Boolean(
        campaignRequest?.patient_services_coordinator
    );

    // Check if primary medical issue is set
    const hasPrimaryMedicalIssue = Boolean(
        campaignRequest?.primary_medical_issue
    );

    // Check for email matches that would block approval
    const hasEmailMatches = emailMatchesSummary?.has_email_matches || false;
    const primaryContactMatches =
        emailMatchesSummary?.primary_contact_matches || [];
    const clientMatches = emailMatchesSummary?.client_matches || [];

    // Generate blocking message for email matches
    const getEmailMatchBlockingMessage = () => {
        const messages = [];
        if (primaryContactMatches.length > 0) {
            messages.push(`Primary contact email matches existing campaign(s)`);
        }
        if (clientMatches.length > 0) {
            messages.push(`Client email matches existing campaign(s)`);
        }
        return messages.join('; ');
    };

    // Debug logging removed - email validation now working correctly

    // Get patient name for display
    const getPatientName = () => {
        const firstName =
            patient?.person?.first_name ||
            campaignRequest?.patient_first_name ||
            campaignRequest?.initial_first_name;
        const middleName =
            patient?.person?.middle_name ||
            campaignRequest?.patient_middle_name ||
            campaignRequest?.initial_middle_name;
        const lastName =
            patient?.person?.last_name ||
            campaignRequest?.patient_last_name ||
            campaignRequest?.initial_last_name;

        const fullName = [firstName, middleName, lastName]
            .filter((e) => e)
            .join(' ');

        return fullName || 'Unknown Patient';
    };

    const handleApproval = async (comments: string) => {
        try {
            setIsApproving(true);
            setApprovalError(null); // Clear any previous errors
            toast.loading('Approving campaign request...');

            if (!campaignRequest?.id) {
                throw new Error('Campaign request ID is required');
            }

            const result = await approveCampaignRequest(
                campaignRequest.id,
                comments
            );

            toast.dismiss();

            // Check for API-level errors
            if (result?.error || result?.detail) {
                const errorMessage = result.error || result.detail;
                setApprovalError(errorMessage);
                setIsApproving(false);
                return; // Stay on page, don't close modal
            }

            // Note: The backend automatically creates an approval note with the comments
            // and includes it in the updated campaign_request data's request_notes field.
            
            // Find the new approval note by comparing request_notes arrays
            if (onNoteAdd && result?.campaign_request?.request_notes) {
                const originalNoteIds = new Set(
                    campaignRequest?.request_notes?.map((note: any) => note.id) || []
                );
                
                // Find notes in the result that weren't in the original request
                const newNotes = result.campaign_request.request_notes.filter(
                    (note: any) => !originalNoteIds.has(note.id)
                );
                
                // Add each new note to the History section
                newNotes.forEach((note: any) => {
                    onNoteAdd(note);
                });
            }

            // Update the campaign request state if callback is provided
            if (onCampaignRequestUpdate && result?.campaign_request) {
                onCampaignRequestUpdate(result.campaign_request);
            }

            // Update the application state if callback is provided and application_id is returned
            if (onApplicationUpdate && result?.application_id) {
                // Create application object with the ID returned from the backend
                onApplicationUpdate({ id: result.application_id } as ApplicationSerializer);
            }

            // Show success message
            toast.success('Campaign request approved successfully!');

            // Close modal after successful approval
            setIsApprovalModalOpen(false);

            // Stay on the Campaign Request Detail page
            // User can navigate to Campaign or Application via the "Go to" buttons if needed
        } catch (error: any) {
            toast.dismiss();
            console.error('Error approving campaign request:', error);
            setApprovalError(error.message || 'Failed to approve campaign request');
            // Stay on page - don't close modal so user can retry
        } finally {
            setIsApproving(false);
        }
    };

    const handleApprovalModalClose = () => {
        setIsApprovalModalOpen(false);
        setApprovalError(null); // Clear error when modal closes
    };

    const handleRejection = async () => {
        try {
            setIsRejecting(true);

            if (!campaignRequest?.id) {
                throw new Error('Campaign request ID is required');
            }

            const result = await rejectCampaignRequest(campaignRequest.id);

            // Update the campaign request state if callback is provided
            if (onCampaignRequestUpdate && result?.campaign_request) {
                onCampaignRequestUpdate(result.campaign_request);
            }

            // Show success message
            toast.success('Campaign request rejected');

            // Close modal after successful rejection
            setIsRejectionModalOpen(false);
        } catch (error: any) {
            console.error('Error rejecting campaign request:', error);
            toast.error(error.message || 'Failed to reject campaign request');
        } finally {
            setIsRejecting(false);
        }
    };

    return (
        <div className='mb-7.5'>
            <Panel>
                <div className='xl:flex xl:justify-between'>
                    <div className='mb-6 xl:mb-0'>
                        <h1 className='mb-4 text-[38px]/[24px] font-medium'>
                            {(() => {
                                const firstName =
                                    patient?.person?.first_name ||
                                    campaignRequest?.patient_first_name ||
                                    campaignRequest?.initial_first_name;
                                const middleName =
                                    patient?.person?.middle_name ||
                                    campaignRequest?.patient_middle_name ||
                                    campaignRequest?.initial_middle_name;
                                const lastName =
                                    patient?.person?.last_name ||
                                    campaignRequest?.patient_last_name ||
                                    campaignRequest?.initial_last_name;

                                const fullName = [
                                    firstName,
                                    middleName,
                                    lastName,
                                ]
                                    .filter((e) => e)
                                    .join(' ');

                                return fullName || 'Unknown Patient';
                            })()}
                        </h1>
                        <div className='flex flex-col gap-2'>
                            <div className='flex items-center'>
                                {(() => {
                                    const statusName =
                                        campaignRequest?.status?.name || 'Unknown';

                                    return (
                                        <StatusBadge
                                            status={statusName}
                                            displayText={statusName}
                                            className='px-2 py-1'
                                        />
                                    );
                                })()}
                            </div>
                            <div className='flex items-center gap-[16px]'>
                                <div className='text-[16px]/[24px] text-[#949599]'>
                                    Last Updated:{' '}
                                    {campaignRequest?.update_date
                                        ? new Date(
                                              campaignRequest.update_date
                                          ).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: '2-digit',
                                              day: '2-digit',
                                          })
                                        : 'Unknown'}
                                </div>
                                {campaignRequest?.created_by_name && (
                                    <div className='text-[16px]/[24px] text-[#949599]'>
                                        Submitted by:{' '}
                                        {campaignRequest.created_by_name}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className='xl:flex xl:flex-col xl:items-end'>
                        <div className='flex flex-nowrap items-center gap-4'>
                            {/* Show navigation buttons for approved requests */}
                            {campaignRequest?.campaign?.id && (
                                <button
                                    onClick={() => {
                                        navigate(`/campaigns/${campaignRequest.campaign.id}`);
                                    }}
                                    className='rounded-full border-[2px] border-primary bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90 whitespace-nowrap'
                                >
                                    Console
                                </button>
                            )}
                            {application?.id && (
                                <button
                                    onClick={() => {
                                        navigate(`/campaign-applications/${application.id}`);
                                    }}
                                    className='rounded-full border-[2px] border-stroke bg-white px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-body transition-colors duration-500 hover:border-bodydark hover:bg-gray-2 whitespace-nowrap'
                                >
                                    Application
                                </button>
                            )}
                            
                            {/* Show approve/reject buttons if status is not Approved or Rejected */}
                            {campaignRequest?.status?.name !== 'Approved' &&
                                campaignRequest?.status?.name !==
                                    'Rejected' && (
                                    <>
                                        <button
                                            onClick={() =>
                                                setIsRejectionModalOpen(true)
                                            }
                                            disabled={isRejecting}
                                            className='rounded-full border-[2px] border-[#F02D3A] bg-[#F02D3A] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] hover:text-black disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap'
                                        >
                                            Reject Request
                                        </button>
                                        <button
                                            onClick={() =>
                                                setIsApprovalModalOpen(true)
                                            }
                                            disabled={
                                                !hasRequiredEmail ||
                                                !hasAssignedCSC ||
                                                !hasPrimaryMedicalIssue ||
                                                hasEmailMatches ||
                                                isApproving
                                            }
                                            className={`rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 whitespace-nowrap ${
                                                !hasRequiredEmail ||
                                                !hasAssignedCSC ||
                                                !hasPrimaryMedicalIssue ||
                                                hasEmailMatches ||
                                                isApproving
                                                    ? 'border-gray-300 bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'border-warning bg-warning text-black hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                                            }`}
                                            title={
                                                !hasAssignedCSC
                                                    ? 'Client Services Coordinator must be assigned to approve this request'
                                                    : !hasRequiredEmail
                                                    ? isPrimarySelf
                                                        ? 'Client email is required to approve this request'
                                                        : 'Primary contact email is required to approve this request'
                                                    : !hasPrimaryMedicalIssue
                                                    ? 'Primary medical issue must be set to approve this request'
                                                    : hasEmailMatches
                                                    ? `Cannot approve: ${getEmailMatchBlockingMessage()}. Please verify this is not a duplicate request.`
                                                    : ''
                                            }
                                        >
                                            Approve Request
                                        </button>
                                    </>
                                )}
                        </div>
                        {/* Only show validation messages if status is not Approved or Rejected */}
                        {campaignRequest?.status?.name !== 'Approved' &&
                            campaignRequest?.status?.name !== 'Rejected' &&
                            (!hasAssignedCSC ||
                                !hasRequiredEmail ||
                                !hasPrimaryMedicalIssue ||
                                hasEmailMatches) && (
                                <div className='text-red-600 mt-2 w-full text-right text-xs'>
                                    {!hasAssignedCSC &&
                                        'Client Services Coordinator must be assigned'}
                                    {!hasRequiredEmail &&
                                        hasAssignedCSC &&
                                        (isPrimarySelf ? 'Client email required' : 'Primary contact email required')}
                                    {!hasPrimaryMedicalIssue &&
                                        hasRequiredEmail &&
                                        hasAssignedCSC &&
                                        'Primary medical issue must be set'}
                                    {hasEmailMatches &&
                                        hasRequiredEmail &&
                                        hasAssignedCSC &&
                                        hasPrimaryMedicalIssue && (
                                            <div className='text-red-600 space-y-1'>
                                                <div className='font-semibold'>
                                                    Email matches found:
                                                </div>
                                                {primaryContactMatches.length >
                                                    0 && (
                                                    <div>
                                                        • Primary contact email
                                                        matches existing
                                                        campaign
                                                    </div>
                                                )}
                                                {clientMatches.length > 0 && (
                                                    <div>
                                                        • Client email matches
                                                        existing campaign
                                                    </div>
                                                )}
                                                <div className='text-red-500 mt-1'>
                                                    Please verify this is not a
                                                    duplicate request.
                                                </div>
                                            </div>
                                        )}
                                </div>
                            )}
                    </div>
                    {/* Hidden: Go To Campaign Detail button
            <button
              onClick={() => {
                if (campaignRequest?.campaign?.id) {
                  navigate(`/campaigns/${campaignRequest.campaign.id}`);
                } else {
                  alert('Campaign not available for this request');
                }
              }}
              className='mt-3 rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white lg:m-auto'
            >
              Go To Campaign Detail
            </button>
            */}
                </div>
            </Panel>

            <ApprovalModal
                isOpen={isApprovalModalOpen}
                onClose={handleApprovalModalClose}
                onConfirm={handleApproval}
                isLoading={isApproving}
                patientName={getPatientName()}
                primaryContactEmail={primaryContactEmailValue}
                clientEmail={clientEmail}
                error={approvalError}
            />

            <RejectionModal
                isOpen={isRejectionModalOpen}
                onClose={() => setIsRejectionModalOpen(false)}
                onConfirm={handleRejection}
                isLoading={isRejecting}
                patientName={getPatientName()}
            />
        </div>
    );
};

export default NameHeader;
